import { useState, useCallback, useEffect } from 'react';
import { useNetworkStore, useSettingsStore, useGameStore, useUIStore } from '@/stores';
import BaseGameSetup from './BaseGameSetup';
import './OnlineGameSetup.css';

/**
 * OnlineGameSetup - オンラインモードセットアップ画面
 * ルーム作成・参加、プレイヤーリスト表示
 */
const OnlineGameSetup = () => {
  const connectionStatus = useNetworkStore((state) => state.connectionStatus);
  const currentRoomId = useNetworkStore((state) => state.roomId);
  const playersInRoom = useNetworkStore((state) => state.playersInRoom);
  const playerName = useSettingsStore((state) => state.player.name);
  const connect = useNetworkStore((state) => state.connect);
  const createRoom = useNetworkStore((state) => state.createRoom);
  const joinRoom = useNetworkStore((state) => state.joinRoom);
  const leaveRoom = useNetworkStore((state) => state.leaveRoom);
  const isHost = useNetworkStore((state) => state.isHost);
  const startGame = useNetworkStore((state) => state.startGame);
  const initializeGame = useGameStore((state) => state.initializeGame);
  const navigateTo = useUIStore((state) => state.navigateTo);

  const [roomIdInput, setRoomIdInput] = useState('');
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);

  /**
   * 初回接続
   */
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      connect();
    }
  }, [connectionStatus, connect]);

  /**
   * ルーム作成ハンドラ
   */
  const handleCreateRoom = useCallback(async () => {
    setIsCreatingRoom(true);
    try {
      await createRoom(playerName);
    } catch (error) {
      console.error('Failed to create room:', error);
    } finally {
      setIsCreatingRoom(false);
    }
  }, [playerName, createRoom]);

  /**
   * ルーム参加ハンドラ
   */
  const handleJoinRoom = useCallback(async () => {
    if (!roomIdInput.trim()) return;

    try {
      await joinRoom(roomIdInput.trim(), playerName);
    } catch (error) {
      console.error('Failed to join room:', error);
    }
  }, [roomIdInput, playerName, joinRoom]);

  /**
   * ルーム退出ハンドラ
   */
  const handleLeaveRoom = useCallback(() => {
    leaveRoom();
  }, [leaveRoom]);

  /**
   * ゲーム開始ハンドラ（ホストのみ）
   */
  const canStartGame = playersInRoom.length === 3 && isHost;

  const handleStartGame = useCallback(() => {
    if (!canStartGame) return;

    // プレイヤー名リストを作成
    const playerNames = playersInRoom.map((p: { id: string; name: string }) => p.name);

    // 自分のプレイヤーIDを特定（1-indexed）
    const myPlayerIndex = playersInRoom.findIndex(
      (p: { id: string; name: string }) => p.name === playerName
    );
    const localPlayerId = String(myPlayerIndex + 1);

    // オンラインモードでゲーム初期化
    initializeGame('online', playerNames, localPlayerId);

    // サーバーにゲーム開始を通知
    startGame();

    // ゲームボード画面へ遷移
    navigateTo('game_board');
  }, [canStartGame, playersInRoom, playerName, initializeGame, startGame, navigateTo]);

  // 接続状態表示
  const renderConnectionStatus = () => {
    switch (connectionStatus) {
      case 'connecting':
        return <div className="OnlineGameSetup__status OnlineGameSetup__status--connecting">
          接続中...
        </div>;
      case 'connected':
        return <div className="OnlineGameSetup__status OnlineGameSetup__status--connected">
          サーバー接続済み
        </div>;
      case 'disconnected':
        return <div className="OnlineGameSetup__status OnlineGameSetup__status--disconnected">
          サーバー未接続
        </div>;
      case 'reconnecting':
        return <div className="OnlineGameSetup__status OnlineGameSetup__status--connecting">
          再接続中...
        </div>;
      default:
        return null;
    }
  };

  const isConnected = connectionStatus === 'connected';

  return (
    <BaseGameSetup
      mode="online"
      title="オンラインプレイ"
      description="オンラインで他のプレイヤーと対戦します。"
    >
      <div className="OnlineGameSetup">
        {/* 接続状態 */}
        {renderConnectionStatus()}

        {/* ルームに参加していない場合 */}
        {!currentRoomId && (
          <div className="OnlineGameSetup__actions">
            <div className="OnlineGameSetup__section">
              <button
                className="OnlineGameSetup__button OnlineGameSetup__button--create"
                onClick={handleCreateRoom}
                disabled={!isConnected || isCreatingRoom}
              >
                {isCreatingRoom ? '作成中...' : '新しいルームを作成'}
              </button>
            </div>

            <div className="OnlineGameSetup__divider">または</div>

            <div className="OnlineGameSetup__section">
              <label className="OnlineGameSetup__label" htmlFor="roomId">
                ルームIDで参加
              </label>
              <div className="OnlineGameSetup__join-form">
                <input
                  id="roomId"
                  type="text"
                  className="OnlineGameSetup__input"
                  value={roomIdInput}
                  onChange={(e) => setRoomIdInput(e.target.value)}
                  placeholder="ルームIDを入力"
                  disabled={!isConnected}
                />
                <button
                  className="OnlineGameSetup__button OnlineGameSetup__button--join"
                  onClick={handleJoinRoom}
                  disabled={!isConnected || !roomIdInput.trim()}
                >
                  参加
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ルームに参加している場合 */}
        {currentRoomId && (
          <div className="OnlineGameSetup__room">
            <div className="OnlineGameSetup__room-info">
              <h3 className="OnlineGameSetup__room-title">ルーム情報</h3>
              <div className="OnlineGameSetup__room-id">
                <span className="OnlineGameSetup__label">ルームID</span>
                <span className="OnlineGameSetup__room-code">{currentRoomId}</span>
              </div>
            </div>

            <div className="OnlineGameSetup__players">
              <h4 className="OnlineGameSetup__players-title">
                プレイヤー ({playersInRoom.length}/3)
              </h4>
              <div className="OnlineGameSetup__player-list">
                {playersInRoom.map((player: { id: string; name: string }, index: number) => (
                  <div key={index} className="OnlineGameSetup__player">
                    👤 {player.name}
                  </div>
                ))}
              </div>
            </div>

            {playersInRoom.length < 3 && (
              <div className="OnlineGameSetup__waiting">
                他のプレイヤーの参加を待っています...
              </div>
            )}

            <button
              className="OnlineGameSetup__button OnlineGameSetup__button--leave"
              onClick={handleLeaveRoom}
            >
              ルームから退出
            </button>

            {/* ゲーム開始ボタン - ホストのみ表示 */}
            {isHost && (
              <button
                className={`OnlineGameSetup__button OnlineGameSetup__button--start ${
                  !canStartGame ? 'OnlineGameSetup__button--disabled' : ''
                }`}
                onClick={handleStartGame}
                disabled={!canStartGame}
              >
                {canStartGame ? 'ゲーム開始' : `待機中 (${playersInRoom.length}/3)`}
              </button>
            )}

            {/* 非ホストへのメッセージ */}
            {!isHost && playersInRoom.length === 3 && (
              <div className="OnlineGameSetup__waiting">
                ホストがゲームを開始するのを待っています...
              </div>
            )}
          </div>
        )}

        {/* 注意事項 */}
        {!isConnected && (
          <div className="OnlineGameSetup__note">
            ⚠️ サーバーに接続できません。ネットワークを確認してください。
          </div>
        )}
      </div>
    </BaseGameSetup>
  );
};

export default OnlineGameSetup;
